import {
  BasicStore,
  DetailedStore,
  type DetailedStoreWithDistance
} from '@spa/components/storefinder/interfaces';
import { useSelector, useStore } from 'react-redux';
import { GeolocationByTextResults } from '@spa/lib/graphql/geolocation/getGeolocationByText';
import { Session } from 'next-auth';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import { changeCurrentCartSelectedStoreRequested } from '../../store/carts/cartsSlice';
import generateActionId from '../../generateActionId';
import getClosestDetailedStoresByGeoLocationAction from '@spa/lib/actions/stores/getClosestDetailedStoresByGeoLocationAction';
import getGeolocationByTextAction from '@spa/lib/actions/geolocation/getGeolocationByTextAction';
import { getSortedInitialDetailedStores } from '@spa/components/storefinder/storefinder.utils';
import getStoreByIdAction from '@spa/lib/actions/stores/getStoreByIdAction';
import getStoreStockForProductAction from '@spa/lib/actions/stores/getStoreStockForProductAction';
import getStoresAction from '@spa/lib/actions/stores/getStoresAction';
import getStoresByIdsAction from '@spa/lib/actions/stores/getStoresByIdsAction';
import selectAccessToken from '../../store/auth/selectors/selectAccessToken';
import selectCollectAtStoreCart from '../../store/carts/selectors/selectCollectAtStoreCart';
import selectLocale from '../../store/settings/selectors/selectLocale';
import setMyStoreIdAction from '@spa/lib/actions/stores/setMyStoreIdAction';
import updateCsrSession from '@spa/lib/auth/updateCsrSession';
import { useCallback } from 'react';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import { useSession } from 'next-auth/react';

export interface UseStoresAPIResult {
  getGeolocation: (searchQuery: string) => Promise<GeolocationByTextResults>;
  getStores: () => Promise<ReadonlyArray<BasicStore>>;
  getClosestDetailedStores: (
    latitude: number,
    longitude: number,
    maximumRadius?: number,
    maximumResults?: number
  ) => Promise<ReadonlyArray<DetailedStoreWithDistance>>;
  getStoreById: (storeId: string) => Promise<DetailedStore>;
  getStoresByIds: (storeIds: Array<string>) => Promise<ReadonlyArray<DetailedStore>>;
  getStoreStockIndicatorForProduct: (productCode: string, storeCode: string) => Promise<boolean>;
  getInitialDetailedStores: (
    allStores: ReadonlyArray<BasicStore>,
    limit?: number
  ) => Promise<ReadonlyArray<DetailedStoreWithDistance>>;
  setMyStoreId: (myStoreId: string, updateSession?: boolean, newToken?: string) => Promise<void>;
  updateFavoriteStore: (refreshedSession: Session | null) => Promise<void>;
  getMyStoreId: () => string | null;
}

export function useStoresAPI(): UseStoresAPIResult {
  const accessToken = useSelector(selectAccessToken);
  const locale = useSelector(selectLocale);
  const collectAtStoreCart = useSelector(selectCollectAtStoreCart);
  const { session }: SessionsData = useNextAuthOrSession();
  const { update } = useSession();
  const store = useStore();

  const syncCartStoreCode = useCallback(
    (storeCode: string): void => {
      if (
        collectAtStoreCart !== null &&
        collectAtStoreCart.deliveryAddress?.storeCode !== storeCode
      ) {
        store.dispatch(
          changeCurrentCartSelectedStoreRequested({
            actionId: generateActionId(),
            storeCode
          })
        );
      }
    },
    [collectAtStoreCart, store]
  );

  const getGeolocation = useCallback(
    async (searchQuery: string): Promise<GeolocationByTextResults> =>
      getGeolocationByTextAction('getGeolocationByTextAction', searchQuery),
    []
  );

  const getStores = useCallback(async (): Promise<ReadonlyArray<BasicStore>> => {
    return await getStoresAction('getStoresAction');
  }, []);

  const getClosestDetailedStores = useCallback(
    async (
      latitude: number,
      longitude: number,
      maximumRadius?: number,
      maximumResults?: number
    ): Promise<ReadonlyArray<DetailedStoreWithDistance>> => {
      return await getClosestDetailedStoresByGeoLocationAction(
        'getClosestDetailedStoresByGeoLocationAction',
        latitude,
        longitude,
        maximumRadius,
        maximumResults
      );
    },
    []
  );

  const getStoreById = useCallback(async (storeId: string): Promise<DetailedStore> => {
    return await getStoreByIdAction('getStoreByIdAction', storeId);
  }, []);

  const getStoresByIds = useCallback(
    async (storeIds: Array<string>): Promise<ReadonlyArray<DetailedStore>> => {
      return await getStoresByIdsAction('getStoresByIdsAction', storeIds);
    },
    []
  );

  const getStoreStockIndicatorForProduct = useCallback(
    async (productCode: string, storeCode: string): Promise<boolean> => {
      return await getStoreStockForProductAction(
        'getStoreStockForProductAction',
        productCode,
        storeCode
      );
    },
    []
  );

  const getInitialDetailedStores = useCallback(
    async (
      allStores: ReadonlyArray<BasicStore>,
      limit?: number
    ): Promise<ReadonlyArray<DetailedStoreWithDistance>> => {
      return await getSortedInitialDetailedStores(allStores, locale, getStoresByIds, limit);
    },
    [getStoresByIds, locale]
  );

  const setMyStoreId = useCallback(
    async (myStoreId: string, updateSession: boolean = true, newToken?: string): Promise<void> => {
      const isAuthenticated: boolean = Boolean(newToken || accessToken);

      if (updateSession) {
        updateCsrSession({
          favoriteStoreId: myStoreId
        });
      }
      if (isAuthenticated) {
        await Promise.all([
          setMyStoreIdAction('setMyStoreIdAction', myStoreId),
          update({
            updateMeData: true
          })
        ]);
      }
      syncCartStoreCode(myStoreId);
    },
    [accessToken, syncCartStoreCode, update]
  );

  const updateFavoriteStore = useCallback(
    async (refreshedSession: Session | null): Promise<void> => {
      const userStoreId: string | null = refreshedSession?.user.myStoreId ?? null;
      const cookieStoreId: string | null = session.favoriteStoreId;

      if (userStoreId && cookieStoreId !== userStoreId) {
        updateCsrSession({ favoriteStoreId: userStoreId });
        syncCartStoreCode(userStoreId);
      } else if (!userStoreId && cookieStoreId && refreshedSession?.accessToken) {
        await setMyStoreId(cookieStoreId, false, refreshedSession.accessToken);
      }
    },
    [session.favoriteStoreId, setMyStoreId, syncCartStoreCode]
  );

  const getMyStoreId = useCallback((): string | null => {
    return session.favoriteStoreId;
  }, [session.favoriteStoreId]);

  return {
    getGeolocation,
    getStores,
    getClosestDetailedStores,
    getStoreById,
    getStoresByIds,
    getStoreStockIndicatorForProduct,
    getInitialDetailedStores,
    setMyStoreId,
    updateFavoriteStore,
    getMyStoreId
  };
}
